/**
 * Builds an AssetBundle (blockstates, models, texture array, colormaps) from the
 * user's own game files: the Minecraft client jar, plus optional mod jars and
 * resource packs layered on top. Nothing from Mojang is shipped with the app.
 */
import type { FileSource } from "@mcp/world";
import { decodePng, type Rgba } from "./png.ts";

export interface TextureArray {
  size: number; // layer width = height
  names: string[]; // "minecraft:block/stone"; layer 0 = missing texture
  pixels: Uint8Array; // layers * size * size * 4
  /** 0 opaque, 1 cutout (binary alpha), 2 translucent */
  alpha: Uint8Array;
  /** average RGB per layer (alpha-weighted) */
  avg: Uint8Array;
}

export interface AssetBundle {
  label: string;
  blockstates: Record<string, unknown>;
  models: Record<string, unknown>;
  textures: TextureArray;
  colormaps: { grass: Uint8Array | null; foliage: Uint8Array | null; dryFoliage: Uint8Array | null };
}

const BS_RE = /^assets\/([^/]+)\/blockstates\/(.+)\.json$/;
const MODEL_RE = /^assets\/([^/]+)\/models\/(.+)\.json$/;

export const EXTRA_TEXTURES = [
  "minecraft:block/water_still",
  "minecraft:block/water_flow",
  "minecraft:block/lava_still",
  "minecraft:block/lava_flow",
];

export function nsId(id: string, defNs = "minecraft") {
  return id.includes(":") ? id : `${defNs}:${id}`;
}

/** packs: highest priority first (resource packs, then mods, then vanilla jar last). */
export async function buildAssetBundle(
  packs: { label: string; src: FileSource }[],
  opts: { size?: number; onProgress?: (msg: string, frac: number) => void } = {},
): Promise<AssetBundle> {
  const size = opts.size ?? 16;
  const progress = opts.onProgress ?? (() => {});
  const td = new TextDecoder();
  const owner = new Map<string, FileSource>(); // asset path -> pack that wins
  for (let i = packs.length - 1; i >= 0; i--) for (const p of packs[i].src.paths()) if (p.startsWith("assets/")) owner.set(p, packs[i].src);

  const readJson = async (path: string) => {
    const s = owner.get(path);
    if (!s) return null;
    try {
      return JSON.parse(td.decode(await s.read(path)));
    } catch {
      return null;
    }
  };

  const blockstates: Record<string, unknown> = {};
  const models: Record<string, unknown> = {};
  const bsPaths: [string, string][] = [];
  const modelPaths: [string, string][] = [];
  for (const path of owner.keys()) {
    let m = BS_RE.exec(path);
    if (m) { bsPaths.push([`${m[1]}:${m[2]}`, path]); continue; }
    m = MODEL_RE.exec(path);
    if (m && (m[2].startsWith("block/") || m[2].startsWith("builtin/"))) modelPaths.push([`${m[1]}:${m[2]}`, path]);
  }
  progress("Reading block states", 0);
  await Promise.all(bsPaths.map(async ([id, p]) => { const j = await readJson(p); if (j) blockstates[id] = j; }));
  progress("Reading block models", 0.15);
  await Promise.all(modelPaths.map(async ([id, p]) => { const j = await readJson(p); if (j) models[id] = normalizeModel(j); }));

  // collect every texture referenced by a block model
  const texSet = new Set<string>(EXTRA_TEXTURES);
  for (const [id, m] of Object.entries(models)) {
    const t = (m as { textures?: Record<string, string> }).textures;
    if (!t) continue;
    const ns = id.split(":")[0];
    for (const v of Object.values(t)) if (typeof v === "string" && !v.startsWith("#")) texSet.add(nsId(v, ns));
  }
  const names = ["missing", ...[...texSet].sort()];
  const layers = names.length;
  const pixels = new Uint8Array(layers * size * size * 4);
  const alpha = new Uint8Array(layers);
  const avg = new Uint8Array(layers * 3);
  writeMissing(pixels, size);
  avg.set([200, 0, 200], 0);

  progress(`Decoding ${layers} textures`, 0.3);
  let done = 0;
  const BATCH = 64;
  for (let b = 1; b < layers; b += BATCH) {
    await Promise.all(
      names.slice(b, b + BATCH).map(async (name, k) => {
        const layer = b + k;
        const [ns, path] = name.split(":");
        const file = `assets/${ns}/textures/${path}.png`;
        const s = owner.get(file);
        let img: Rgba | null = null;
        if (s) {
          try { img = await decodePng(await s.read(file)); } catch { img = null; }
        }
        writeLayer(img, { pixels, alpha, avg }, layer, size);
      }),
    );
    done = Math.min(layers, b + BATCH);
    progress(`Decoding textures ${done}/${layers}`, 0.3 + 0.65 * (done / layers));
  }

  const cm = async (n: string) => {
    const f = `assets/minecraft/textures/colormap/${n}.png`;
    const s = owner.get(f);
    if (!s) return null;
    try { return (await decodePng(await s.read(f))).data; } catch { return null; }
  };
  const colormaps = { grass: await cm("grass"), foliage: await cm("foliage"), dryFoliage: await cm("dry_foliage") };
  progress("Assets ready", 1);
  return { label: packs.map((p) => p.label).join(" + "), blockstates, models, textures: { size, names, pixels, alpha, avg }, colormaps };
}

/** One texture into its layer of the array (missing image: a copy of the "missing" layer). */
function writeLayer(img: Rgba | null, t: Pick<TextureArray, "pixels" | "alpha" | "avg">, layer: number, size: number) {
  if (!img) {
    t.pixels.copyWithin(layer * size * size * 4, 0, size * size * 4);
    t.avg.set([200, 0, 200], layer * 3);
    return;
  }
  blit(img, t.pixels, layer, size);
  const st = stats(t.pixels, layer, size);
  if (st.cls !== 0) bleedAlpha(t.pixels, layer, size, st.avg);
  t.alpha[layer] = st.cls;
  t.avg.set(st.avg, layer * 3);
}

/**
 * A texture array from images already in memory (MC Planner's own built-in blocks, lib/demoBlocks.ts), laid out
 * like the one built from a game jar: layer 0 = missing, then the names sorted.
 */
export function textureArrayFrom(images: Map<string, Rgba>, size = 16): TextureArray {
  const names = ["missing", ...[...new Set([...images.keys(), ...EXTRA_TEXTURES])].sort()];
  const t: TextureArray = { size, names, pixels: new Uint8Array(names.length * size * size * 4), alpha: new Uint8Array(names.length), avg: new Uint8Array(names.length * 3) };
  writeMissing(t.pixels, size);
  t.avg.set([200, 0, 200], 0);
  for (let i = 1; i < names.length; i++) writeLayer(images.get(names[i]) ?? null, t, i, size);
  return t;
}

/** 26.x allows `{"sprite": "...", "force_translucent": true}` texture values; flatten to strings. */
function normalizeModel(j: { textures?: Record<string, unknown> }) {
  if (j.textures) {
    for (const [k, v] of Object.entries(j.textures)) {
      if (v && typeof v === "object" && typeof (v as { sprite?: unknown }).sprite === "string") j.textures[k] = (v as { sprite: string }).sprite;
      else if (typeof v !== "string") delete j.textures[k];
    }
  }
  return j;
}

function writeMissing(px: Uint8Array, size: number) {
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++) {
      const o = (y * size + x) * 4;
      const on = (x < size / 2) !== (y < size / 2);
      px[o] = on ? 248 : 0;
      px[o + 1] = 0;
      px[o + 2] = on ? 248 : 0;
      px[o + 3] = 255;
    }
}

/** Copy first animation frame (square top) into the layer, resampling to `size` (box filter / nearest). */
function blit(img: Rgba, px: Uint8Array, layer: number, size: number) {
  const w = img.width;
  const h = Math.min(img.height, img.width); // animated strips: first frame
  const base = layer * size * size * 4;
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++) {
      const o = base + (y * size + x) * 4;
      if (w === size && h === size) {
        const i = (y * w + x) * 4;
        px[o] = img.data[i]; px[o + 1] = img.data[i + 1]; px[o + 2] = img.data[i + 2]; px[o + 3] = img.data[i + 3];
        continue;
      }
      // box filter over the source footprint (handles HD packs) or nearest for upscaling
      const x0 = Math.floor((x * w) / size), x1 = Math.max(x0 + 1, Math.floor(((x + 1) * w) / size));
      const y0 = Math.floor((y * h) / size), y1 = Math.max(y0 + 1, Math.floor(((y + 1) * h) / size));
      let r = 0, g = 0, b = 0, a = 0, n = 0;
      for (let yy = y0; yy < y1; yy++)
        for (let xx = x0; xx < x1; xx++) {
          const i = (yy * w + xx) * 4;
          const aa = img.data[i + 3];
          r += img.data[i] * aa; g += img.data[i + 1] * aa; b += img.data[i + 2] * aa; a += aa; n++;
        }
      px[o] = a ? r / a : 0; px[o + 1] = a ? g / a : 0; px[o + 2] = a ? b / a : 0; px[o + 3] = a / n;
    }
}

/**
 * Give fully transparent texels the colour of their opaque neighbours (they stay transparent).
 * Otherwise GPU mipmapping averages in their (usually black) RGB and cut-out textures such as
 * leaves, grass and overlays turn dark with distance.
 */
function bleedAlpha(px: Uint8Array, layer: number, size: number, avg: number[]) {
  const base = layer * size * size * 4;
  const filled = new Uint8Array(size * size);
  for (let i = 0; i < size * size; i++) filled[i] = px[base + i * 4 + 3] > 0 ? 1 : 0;
  for (let pass = 0; pass < size; pass++) {
    let changed = false;
    const next = filled.slice();
    for (let y = 0; y < size; y++)
      for (let x = 0; x < size; x++) {
        const i = y * size + x;
        if (filled[i]) continue;
        let r = 0, g = 0, b = 0, n = 0;
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const nx = x + dx, ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= size || ny >= size || !filled[ny * size + nx]) continue;
          const o = base + (ny * size + nx) * 4;
          r += px[o]; g += px[o + 1]; b += px[o + 2]; n++;
        }
        if (!n) continue;
        const o = base + i * 4;
        px[o] = r / n; px[o + 1] = g / n; px[o + 2] = b / n;
        next[i] = 1;
        changed = true;
      }
    filled.set(next);
    if (!changed) break;
  }
  for (let i = 0; i < size * size; i++) {
    if (filled[i]) continue;
    const o = base + i * 4;
    px[o] = avg[0]; px[o + 1] = avg[1]; px[o + 2] = avg[2];
  }
}

function stats(px: Uint8Array, layer: number, size: number) {
  const base = layer * size * size * 4;
  let r = 0, g = 0, b = 0, aw = 0, zero = 0, partial = 0;
  for (let i = 0; i < size * size; i++) {
    const o = base + i * 4;
    const a = px[o + 3];
    if (a === 0) zero++;
    else if (a < 250) partial++;
    r += px[o] * a; g += px[o + 1] * a; b += px[o + 2] * a; aw += a;
  }
  const cls = partial > size * size * 0.05 ? 2 : zero > 0 || partial > 0 ? 1 : 0;
  return { cls, avg: aw ? [r / aw, g / aw, b / aw] : [0, 0, 0] };
}
